import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Plus, Truck, Trash2, Container, Pencil, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT_CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";

const VEHICLE_TYPES = ["Truck", "Tempo", "Mini Truck", "Container", "Pickup", "Other"];
const STATUSES = ["Open", "Confirmed", "In Transit", "Delivered", "Completed", "On Hold"];

const STATUS_CLS = {
  "Open":       "bg-blue-50 text-blue-700 border-blue-200",
  "Confirmed":  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "In Transit": "bg-amber-50 text-amber-700 border-amber-200",
  "Complete":   "bg-green-50 text-green-700 border-green-200",
  "Completed":  "bg-green-50 text-green-700 border-green-200",
  "Delivered":  "bg-green-50 text-green-700 border-green-200",
  "On Hold":    "bg-red-50 text-red-600 border-red-200",
};

const EMPTY_FORM = {
  consignment_date: "",
  vehicle_id: "",
  vehicle_display: "",
  vehicle_type: "",
  closed_vehicle: false,
  is_temp_vehicle: false,
  temp_vehicle_number: "",
  temp_vehicle_type: "",
  status: "Open",
  origin_type: "warehouse",
  origin_label: "",
  destination_type: "warehouse",
  destination_label: "",
};

function FormField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">{label}</label>
      {children}
    </div>
  );
}

export default function ConsignmentsPage() {
  const navigate = useNavigate();
  const completed = useLocation().pathname.endsWith("/completed");
  const { user } = useAuth();
  const isWM = user?.role === "warehouse_manager";
  const isRM = user?.role === "relationship_manager";
  const isSupervisor = user?.role === "supervisor";
  const canDelete = ["admin", "superadmin", "manager"].includes(user?.role);
  const [consignments, setConsignments] = useState([]);
  const [vehicles, setVehicles]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [editConsignment, setEditConsignment] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const loadMoreRef = useRef(null);

  const visibleTrips = consignments;

  const fetchConsignments = async (requestedPage = 1, append = false) => {
    if (append && loadingRef.current) return;
    if (append) { loadingRef.current = true; setLoadingMore(true); }
    try {
      const { data } = await axios.get(`${API}/trips/paginated`, { params: { page: requestedPage, limit: 50, completed }, withCredentials: true });
      setConsignments(previous => append ? [...previous, ...data.items] : data.items);
      setPage(data.page); setTotal(data.total); setHasMore(data.has_more);
    } catch { toast.error("Failed to load trip sheets."); }
    finally { setLoading(false); if (append) { loadingRef.current = false; setLoadingMore(false); } }
  };

  useEffect(() => {
    setLoading(true);
    fetchConsignments(1, false);
  }, [completed]);

  useEffect(() => {
    axios.get(`${API}/vehicles`, { withCredentials: true })
      .then(r => setVehicles(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && !loadingRef.current) fetchConsignments(page + 1, true);
    }, { rootMargin: "250px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, page, completed]);

  const openEditConsignment = (c) => {
    setEditConsignment(c);
    setForm({
      consignment_date:    c.consignment_date || "",
      vehicle_id:          c.vehicle_id || "",
      vehicle_display:     c.vehicle_display || "",
      vehicle_type:        c.vehicle_type || "",
      closed_vehicle:      c.closed_vehicle || false,
      is_temp_vehicle:     c.is_temp_vehicle || false,
      temp_vehicle_number: c.temp_vehicle_number || "",
      temp_vehicle_type:   c.temp_vehicle_type || "",
      status:              c.status || "Open",
      origin_type:         c.origin_type || "warehouse",
      origin_label:        c.origin_label || "",
      destination_type:    c.destination_type || "warehouse",
      destination_label:   c.destination_label || "",
    });
    setShowAdd(true);
  };

  const handleVehicleSelect = (vehicleId) => {
    const v = vehicles.find(v => v.id === vehicleId);
    if (!v) return;
    setForm(f => ({
      ...f,
      vehicle_id: vehicleId,
      vehicle_type: v.vehicle_type || "",
      closed_vehicle: Boolean(v.closed_vehicle),
      vehicle_display: `${v.vehicle_name} (${v.vehicle_number})`,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consignment_date) { toast.error("Consignment date is required."); return; }
    if (!form.is_temp_vehicle && !form.vehicle_id) { toast.error("Select a vehicle or use a temporary vehicle."); return; }
    if (form.is_temp_vehicle && !form.temp_vehicle_number.trim()) { toast.error("Temporary vehicle number is required."); return; }

    const payload = { ...form };
    if (form.is_temp_vehicle) {
      payload.vehicle_id = "";
      payload.vehicle_type = form.temp_vehicle_type;
      payload.closed_vehicle = false;
      payload.vehicle_display = `${form.temp_vehicle_number.trim()} (${form.temp_vehicle_type || "Temp"})`;
    }

    setSaving(true);
    try {
      if (editConsignment) {
        const { data } = await axios.patch(`${API}/trips/${editConsignment.id}`, payload, { withCredentials: true });
        toast.success(`Consignment ${data.consignment_number} updated!`);
      } else {
        const { data } = await axios.post(`${API}/trips`, payload, { withCredentials: true });
        toast.success(`Consignment ${data.consignment_number} created!`);
      }
      await fetchConsignments(1, false);
      setShowAdd(false);
      setEditConsignment(null);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save consignment.");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this consignment?")) return;
    try {
      await axios.delete(`${API}/trips/${id}`, { withCredentials: true });
      setConsignments(prev => prev.filter(c => c.id !== id));
      setTotal(value => Math.max(0, value - 1));
      toast.success("Trip sheet deleted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete.");
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">{completed ? "Completed" : "Active"} Trip Sheets</h2>
          <p className="text-xs text-[#475569] mt-0.5">
            {total} trip sheet{total !== 1 ? "s" : ""} · {visibleTrips.length} loaded — each represents one vehicle movement
          </p>
        </div>
        {!isRM && !isSupervisor && <Button
          onClick={() => { setForm(EMPTY_FORM); setShowAdd(true); }}
          className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]"
        >
          <Plus size={14} className="mr-1.5" />Create Trip Sheet
        </Button>}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">Loading...</div>
        ) : visibleTrips.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Container size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">
              No trip sheets yet.{" "}
              {!isRM && !isSupervisor && <button onClick={() => setShowAdd(true)} className="text-[#1B2A6B] font-semibold underline">
                Create your first consignment
              </button>}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                {["#", "Trip Sheet No.", "Vehicle", "Status", ""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleTrips.map((c, idx) => {
                const rowBg = idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]";
                return (
                  <React.Fragment key={c.id}>
                    <tr className={rowBg}>
                      <td className="px-4 pt-3 pb-1 text-[#94A3B8] text-xs font-mono w-8">{idx + 1}</td>
                      <td className="px-4 pt-3 pb-1 whitespace-nowrap">
                        <span className="font-mono font-black text-sm text-[#1B2A6B] bg-[#EEF0FA] px-2.5 py-1 border border-[#C7D2FE] tracking-widest">
                          {c.consignment_number}
                        </span>
                      </td>
                      <td className="px-4 pt-3 pb-1">
                        <div className="flex items-center gap-1.5 text-sm text-[#0F172A]">
                          <Truck size={12} className="text-[#94A3B8] shrink-0" />
                          {c.vehicle_display || "—"}
                          {c.is_temp_vehicle && (
                            <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-sm">Temp</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 pt-3 pb-1">
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_CLS[c.status] || STATUS_CLS["Open"]}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 pt-3 pb-1">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(`/trips/${c.id}`)}
                            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1B2A6B] hover:text-white border border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-2.5 py-1.5 rounded-sm"
                            title="View details"
                          >
                            <Eye size={11} />View
                          </button>
                          {!isRM && <button
                            onClick={() => openEditConsignment(c)}
                            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#475569] hover:text-[#1B2A6B] border border-[#E2E8F0] hover:border-[#1B2A6B] transition-colors px-2.5 py-1.5 rounded-sm"
                            title="Edit consignment"
                          >
                            <Pencil size={11} />Edit
                          </button>}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(c.id)}
                              className="text-[#CBD5E1] hover:text-red-500 transition-colors p-1"
                              title="Delete consignment"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Row 2 — secondary details */}
                    <tr className={`border-b border-[#E2E8F0] ${rowBg}`}>
                      <td colSpan={5} className="px-4 pt-0.5 pb-3">
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-0.5 text-xs text-[#64748B]">
                          {c.consignment_date && (
                            <span><span className="font-medium text-[#475569]">Date:</span> {format(new Date(c.consignment_date + "T00:00:00"), "dd MMM yyyy")}</span>
                          )}
                          <span>
                            <span className="font-medium text-[#475569]">Consignments:</span>{" "}
                            {c.transfer_count} transfer{c.transfer_count !== 1 ? "s" : ""}
                          </span>
                          {c.vehicle_type && (
                            <span><span className="font-medium text-[#475569]">Type:</span> {c.vehicle_type}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center text-xs text-[#64748B]">
        {loadingMore ? "Loading 50 more trip sheets..." : hasMore ? "Scroll to load more" : visibleTrips.length ? "All trip sheets loaded" : null}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={o => { if (!o) { setForm(EMPTY_FORM); setEditConsignment(null); } setShowAdd(o); }}>
        <DialogContent className="max-w-md rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">
              {editConsignment ? "Edit Trip Sheet" : "Create New Trip Sheet"}
            </DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">
              {editConsignment ? editConsignment.consignment_number : "One vehicle movement carrying one or more consignments"}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4">
              <FormField label="Trip Sheet Date *">
                <Input
                  type="date"
                  value={form.consignment_date}
                  onChange={e => setForm(f => ({ ...f, consignment_date: e.target.value }))}
                  className={INPUT_CLS}
                />
              </FormField>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Origin Type">
                  <Select value={form.origin_type} onValueChange={v => setForm(f => ({ ...f, origin_type: v }))}>
                    <SelectTrigger className={INPUT_CLS}><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="from_location">Customer Pickup</SelectItem>
                      <SelectItem value="warehouse">Warehouse</SelectItem>
                      <SelectItem value="to_location">Delivery Point</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Destination Type">
                  <Select value={form.destination_type} onValueChange={v => setForm(f => ({ ...f, destination_type: v }))}>
                    <SelectTrigger className={INPUT_CLS}><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="warehouse">Warehouse</SelectItem>
                      <SelectItem value="to_location">Customer Delivery Point</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Origin">
                  <Input value={form.origin_label} onChange={e => setForm(f => ({ ...f, origin_label: e.target.value }))}
                    placeholder="Starting point / godown" className={INPUT_CLS} />
                </FormField>
                <FormField label="Destination">
                  <Input value={form.destination_label} onChange={e => setForm(f => ({ ...f, destination_label: e.target.value }))}
                    placeholder="Endpoint / godown" className={INPUT_CLS} />
                </FormField>
              </div>

              {/* Vehicle toggle */}
              <div className="flex items-center gap-3 py-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_temp_vehicle}
                    onChange={e => setForm(f => ({ ...f, is_temp_vehicle: e.target.checked, vehicle_id: "", vehicle_display: "" }))}
                    className="accent-[#1B2A6B] w-4 h-4"
                  />
                  <span className="text-xs font-bold text-[#0F172A] uppercase tracking-widest">Use Temporary Vehicle</span>
                </label>
              </div>

              {form.is_temp_vehicle ? (
                <>
                  <FormField label="Vehicle Number *">
                    <Input
                      value={form.temp_vehicle_number}
                      onChange={e => setForm(f => ({ ...f, temp_vehicle_number: e.target.value }))}
                      placeholder="e.g. MH-01-AB-1234"
                      className={INPUT_CLS}
                    />
                  </FormField>
                  <FormField label="Vehicle Type">
                    <Select value={form.temp_vehicle_type} onValueChange={v => setForm(f => ({ ...f, temp_vehicle_type: v }))}>
                      <SelectTrigger className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {VEHICLE_TYPES.map(t => <SelectItem key={t} value={t} className="rounded-none">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                </>
              ) : (
                <FormField label="Select Vehicle *">
                  <Select value={form.vehicle_id} onValueChange={handleVehicleSelect}>
                    <SelectTrigger className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                      <SelectValue placeholder={vehicles.length === 0 ? "No vehicles — add via Master Data" : "Select a vehicle"} />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {vehicles.map(v => (
                        <SelectItem key={v.id} value={v.id} className="rounded-none">
                          <span className="font-medium">{v.vehicle_name}</span>
                          <span className="text-[#94A3B8] ml-2 text-xs">({v.vehicle_number})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              )}

              <FormField label="Status">
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {STATUSES.map(s => <SelectItem key={s} value={s} className="rounded-none">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <div className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)}
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">
                Cancel
              </button>
              <Button type="submit" disabled={saving}
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
                {saving ? "Saving..." : (editConsignment ? "Save Changes" : "Create Trip Sheet")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
